import { Fragment, useRef } from 'react';
import {
    Dialog,
    DialogPanel,
    Transition,
    TransitionChild,
} from '@headlessui/react';
import { useLocation } from 'react-router-dom';
import { useModal } from 'flow/contexts/ModalProvider';
import { TypedMap } from 'flow/types/TypedMap';
import { Message } from 'flow/types/Chats';
import { ChatSessionKey } from 'flow/types/EntityKeys';
import ChatPostDisplay, { pathnameIsOverview } from './ChatPostDisplay';
import ChatPostCommentsList from './ChatPostCommentsList';
import ChatPostCommentInput from './ChatPostCommentInput';
import ChatPostFooter from './ChatPostFooter';
import { twMerge } from 'tailwind-merge';
import useSinglePaneMode from 'flow/hooks/useSinglePaneMode';

interface Props {
    message?: TypedMap<Message>;
    sessionKey?: ChatSessionKey;
}

const ChatPostModal = ({ message, sessionKey }: Props) => {
    const { closeModal } = useModal();
    const { pathname } = useLocation();

    const placeholderAfterCommentsRef = useRef<HTMLDivElement | null>(null);

    const { isSinglePaneMode } = useSinglePaneMode();

    if (!message || !sessionKey) {
        return null;
    }

    const scrollSmoothlyToEnd = () =>
        setTimeout(() => {
            placeholderAfterCommentsRef.current?.scrollIntoView({
                behavior: 'smooth',
                block: 'nearest',
                inline: 'nearest',
            });
        }, 0);

    return (
        <Transition show={true} as={Fragment}>
            <Dialog
                static={true}
                as="div"
                className="relative z-20"
                onClose={closeModal}
            >
                <TransitionChild
                    as={Fragment}
                    enter="ease-out duration-300"
                    enterFrom="opacity-0"
                    enterTo="opacity-100"
                    leave="ease-in duration-200"
                    leaveFrom="opacity-100"
                    leaveTo="opacity-0"
                >
                    <div className="fixed inset-0 bg-gray-900 opacity-70 transition-opacity dark:bg-gray-100" />
                </TransitionChild>

                <div className="fixed inset-0 z-30 w-screen overflow-y-auto">
                    <div className="flex h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
                        <TransitionChild
                            as={Fragment}
                            enter="ease-out duration-300"
                            enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                            enterTo="opacity-100 translate-y-0 sm:scale-100"
                            leave="ease-in duration-200"
                            leaveFrom="opacity-100 translate-y-0 sm:scale-100"
                            leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                        >
                            <DialogPanel
                                className={twMerge(
                                    isSinglePaneMode
                                        ? 'fixed h-full w-full'
                                        : 'relative flex max-w-2xl',
                                    'dark:bg-gray-100s w-2xl transform flex-col overflow-hidden rounded-sm bg-white text-left shadow-lg transition-all',
                                )}
                            >
                                <div
                                    className={twMerge(
                                        isSinglePaneMode
                                            ? 'fixed h-full w-full overflow-y-scroll'
                                            : 'flex max-h-[90dvh]',
                                        'flex-col sm:min-w-[640px]',
                                    )}
                                >
                                    <div className="scrollbar shrink">
                                        <ChatPostDisplay
                                            message={message}
                                            sessionKey={sessionKey}
                                            inOverview={pathnameIsOverview(
                                                pathname,
                                            )}
                                        />
                                        <ChatPostCommentsList
                                            message={message}
                                            sessionKey={sessionKey}
                                        />
                                        <ChatPostCommentInput
                                            message={message}
                                            sessionKey={sessionKey}
                                        />
                                        <div
                                            className="bottom-0 block"
                                            ref={placeholderAfterCommentsRef}
                                        />
                                    </div>

                                    <ChatPostFooter
                                        message={message}
                                        sessionKey={sessionKey}
                                        goToComment={() => {
                                            scrollSmoothlyToEnd();
                                        }}
                                    />
                                </div>
                            </DialogPanel>
                        </TransitionChild>
                    </div>
                </div>
            </Dialog>
        </Transition>
    );
};

export default ChatPostModal;
